<script lang="ts">
	import type { Column } from '@isoftdata/svelte-table'
	import type { InventoryOptionsConfig$result } from '$houdini'
	import type { InventoryType } from 'utility/search-fields'
	import type { i18n } from 'types/common'

	import CustomQuestionModal from './CustomQuestionModal.svelte'
	import { Table, Td } from '@isoftdata/svelte-table'
	import Button from '@isoftdata/svelte-button'
	import Checkbox from '@isoftdata/svelte-checkbox'

	import BooleanIndicator from 'components/BooleanIndicator.svelte'
	import { upsert } from '@isoftdata/utility-array'
	import { tick } from 'svelte'
	import { getContext } from 'svelte'

	type InventoryOption = Omit<InventoryOptionsConfig$result['inventoryOptions'][number], ' $fragments'> & {
		' $fragments'?: unknown
		hasChoices: boolean
	}

	interface Props {
		selectedInventoryTypeId: number | undefined
		questions: Array<InventoryOption>
		dataTypesMap: Map<string, string>
		inventoryTypes: Array<InventoryType>
	}

	let { selectedInventoryTypeId, questions = $bindable(), dataTypesMap, inventoryTypes }: Props = $props()

	let customQuestionModal: CustomQuestionModal | undefined = $state(undefined)
	let selectedQuestion: InventoryOption | null = $state(null)
	let showGlobalQuestions = $state(true)

	const { t: translate } = getContext<i18n>('i18next')

	const columns: Array<Column<InventoryOption>> = [
		{ property: 'name', name: translate('common:question', 'Question'), minWidth: '250px' },
		{ property: 'inventoryType[name]', name: translate('common:type', 'Type') },
		{ property: 'manufacturer[name]', name: translate('common:manufacturer', 'Manufacturer') },
		{ property: 'model[name]', name: translate('common:model', 'Model') },
		{ property: 'category[name]', name: translate('common:category', 'Category') },
		{ property: 'dataType', name: translate('common:dataType', 'Data Type') },
		{
			property: 'public',
			name: translate('attachments.selectionButtonPublic', 'Public'),
			align: 'center',
			width: '1rem',
		},
		{ property: 'required', name: translate('common:required', 'Required'), align: 'center', width: '1rem' },
		{ property: 'hasChoices', name: translate('common:choices', 'Choices') },
	]
</script>

<Table
	responsive
	stickyHeader
	filterEnabled
	columnHidingEnabled
	{columns}
	idProp="id"
	parentStyle="overflow-y: auto; max-height: 50vh;"
	rows={questions}
>
	{#snippet header()}
		<div class="d-flex align-items-end">
			<div class="btn-group me-2">
				<Button
					outline
					size="sm"
					color="success"
					iconClass="plus"
					title={translate('common:add', 'Add')}
					onclick={() => {
						customQuestionModal?.add()
					}}>{translate('common:add', 'Add')}</Button
				>

				<Button
					outline
					colorGreyDisabled
					size="sm"
					color="primary"
					iconClass="pencil"
					title={translate('configuration.inventorytype.editQuestion', 'Edit Question')}
					disabled={!selectedQuestion}
					onclick={() => {
						if (selectedQuestion) {
							customQuestionModal?.edit(selectedQuestion)
						}
					}}>{translate('configuration.inventorytype.editQuestion', 'Edit Question')}</Button
				>
			</div>
			<Checkbox
				label={translate('configuration.inventorytype.showGlobalQuestions', 'Show Global Questions')}
				bind:checked={showGlobalQuestions}
			/>
		</div>
	{/snippet}

	{#snippet children({ row })}
		{#if showGlobalQuestions || row.inventoryType}
			<tr
				id="question-{row.id}"
				class="cursor-pointer"
				class:table-primary={selectedQuestion?.id === row.id}
				onclick={() => (selectedQuestion = questions[row.originalIndex])}
			>
				<Td property="name">{row.name}</Td>
				<Td
					class="text-nowrap"
					property="inventoryType[name]"
					>{row.inventoryType === null
						? translate('configuration.inventorytype.allInventoryTypes', 'All Inventory Types')
						: `${row.inventoryType?.name} - ${row.inventoryType?.id}`}</Td
				>
				<Td property="manufacturer[name]">{row.manufacturer?.name || ''}</Td>
				<Td property="model[name]">{row.model?.name || ''}</Td>
				<Td property="category[name]">{row.category?.name || ''}</Td>
				<Td property="dataType">{dataTypesMap.get(row.dataType)}</Td>
				<Td property="public"><BooleanIndicator value={row.public}></BooleanIndicator></Td>
				<Td property="required"><BooleanIndicator value={row.required}></BooleanIndicator></Td>
				<Td
					property="hasChoices"
					class="text-nowrap"
					>{#if row.choiceQuery || row.choices.length}
						{row.choiceQuery ? 'Choice Query' : ''}
						{row.choiceQuery && row.choices.length ? ' & ' : ''}
						{row.choices.length ? `${row.choices.length} Choice(s)` : ''}
					{:else}
						<i class="text-body-secondary">{translate('common:notSlashAcceptable', 'N/A')}</i>
					{/if}
				</Td>
			</tr>
		{/if}
	{/snippet}
	{#snippet noRows({ visibleColumnsCount })}
		<tr>
			<td colspan={visibleColumnsCount}>
				{translate('configuration.inventorytype.noMatchingQuestionsFound.', 'No matching questions found.')}</td
			>
		</tr>
	{/snippet}
</Table>

<CustomQuestionModal
	{dataTypesMap}
	{selectedInventoryTypeId}
	{inventoryTypes}
	bind:this={customQuestionModal}
	questionSaved={async savedQuestion => {
		const question = {
			...savedQuestion,
			hasChoices: !!savedQuestion.choiceQuery || savedQuestion.choices.length > 0,
		}
		questions = upsert(questions, 'id', question)
		selectedQuestion = question
		await tick()
		document.getElementById(`question-${question.id}`)?.scrollIntoView({ block: 'center' })
	}}
/>
